import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  Monitor, PlayCircle, Smartphone, User2,
  CalendarClock, ClipboardCheck, RotateCcw, BadgeDollarSign, AlertTriangle,
  Star, ChevronLeft, ChevronRight, Sparkles,
} from "lucide-react";
import { useT } from "../i18n/I18nProvider";
import { OWNER_EXPLAINERS } from "../pages/public/ownerExplainers";
import BrandOutro from "./walkthrough2/BrandOutro";

/**
 * The Business-homepage demo, NOT a custom-built simulation. Every step's
 * right-hand preview is a real, unmodified /demo-business/scenarios/:id page
 * (the exact same engine used by the public scenario catalog), rendered
 * through an iframe exactly like the renter walkthrough's DeviceFrame does.
 * Once started, the homepage deliberately shows only that phone/desktop
 * model and its device switch, plus a small manual Previous/Next fallback.
 *
 * ONE demo section, with a Normal/Issue mode selector (`MODES`) switching
 * the content shown inside it, not two separate homepage blocks. The two
 * stories stay functionally independent under the hood: separate step
 * arrays, separate testIdBase, and `key={modeKey}` on WalkthroughPlayer
 * forces a clean remount (fresh stepIndex/deviceMode) on every switch:
 *   - normal (MAIN_STEPS): the everyday lifecycle, inquiry through the
 *     review that closes out a completed rental.
 *   - issue (ISSUE_STEPS): "what if something goes wrong" (a vehicle
 *     breakdown): a different, shorter story, not a step in the happy path.
 * Every embedded page loads with `?embed=1`, which (scoped ONLY to these
 * homepage widgets: see PublicScenarioStage.jsx / DemoStage.jsx /
 * DemoWorkflow.jsx / CustomWorkflow.jsx / DemoPanels.jsx) strips that page's
 * own header, top bar, explainer, sales notes, auto-play/CTA row and
 * end-of-scenario recap down to just the interactive phone/desktop content,
 * and speeds up + resumes (instead of replaying) chat animations. The
 * standalone public catalog and the promoter/company-dashboard demo tool
 * are untouched.
 */
const MAIN_STEPS = [
  {
    key: "inquiry",
    scenarioId: "messenger-inquiry",
    icon: User2,
    eyebrow: "1 · Getting the inquiry",
    title: "A booking request comes in over Messenger",
  },
  {
    key: "pickup-prep",
    scenarioId: "upcoming-pickup",
    icon: CalendarClock,
    eyebrow: "2 · Getting ready for pickup",
    title: "Staff get a prep reminder before the customer arrives",
  },
  {
    key: "pickup-handover",
    scenarioId: "pickup-handover",
    icon: ClipboardCheck,
    eyebrow: "3 · Pickup handover",
    title: "Odometer, fuel, condition and keys: recorded before the customer drives off",
  },
  {
    key: "return-ready",
    scenarioId: "return-and-ready",
    icon: RotateCcw,
    eyebrow: "4 · Return & vehicle ready again",
    title: "The vehicle comes back: inspected, completed, and available again",
  },
  {
    key: "deposit",
    scenarioId: "deposit-balance",
    icon: BadgeDollarSign,
    eyebrow: "5 · Recording the balance",
    title: "Deposits and balances stay on the booking record",
  },
  {
    key: "review",
    scenarioId: "verified-review",
    icon: Star,
    eyebrow: "6 · Review invite",
    title: "A review invite goes out once the booking is completed",
  },
  {
    key: "outro",
    isOutro: true,
    icon: Sparkles,
    eyebrow: "ConfirmPH",
    title: "That's the everyday ConfirmPH workflow",
    isLast: true,
  },
];

const ISSUE_STEPS = [
  {
    key: "issue",
    scenarioId: "vehicle-breakdown",
    icon: AlertTriangle,
    eyebrow: "The scenario",
    title: "A vehicle breaks down before a booking: see how ConfirmPH handles it",
  },
  {
    key: "outro",
    isOutro: true,
    icon: Sparkles,
    eyebrow: "ConfirmPH",
    title: "That's the incident-to-replacement workflow",
    isLast: true,
  },
];

// One demo section, two choices: a visitor picks which story to watch and
// the choice swaps the content inside this same section. The two stories
// stay functionally independent (own steps, own testIdBase, own player
// state via `key={mode}`); only the outer presentation is unified.
// Labels/teasers/CTAs are resolved via t() at render time (labelKey etc.)
// rather than baked in here, so this section reads correctly in both
// English and Filipino: see biz.proof.* in i18n/translations.js. The
// longer per-mode descriptions were dropped from display in favor of one
// fixed intro line (biz.proof.title/subtitle-equivalent lives in the
// section header below); teasers stay per-mode.
const MODES = [
  {
    key: "normal",
    labelKey: "biz.proof.mode.normal",
    steps: MAIN_STEPS,
    testIdBase: "biz-walkthrough",
    teaser: "Walk through a complete rental: inquiry, pickup, return, and the review that closes it out. Real screens, nothing leaves this page.",
    ctaKey: "biz.proof.cta.normal",
  },
  {
    key: "issue",
    labelKey: "biz.proof.mode.issue",
    steps: ISSUE_STEPS,
    testIdBase: "biz-issue-walkthrough",
    teaser: "See a vehicle breakdown handled from report to replacement, in the real ConfirmPH dashboard. Real screens, nothing leaves this page.",
    ctaKey: "biz.proof.cta.issue",
  },
];

export default function BusinessProductWalkthrough() {
  const { t } = useT();
  const [modeKey, setModeKey] = useState("normal");
  const [started, setStarted] = useState(false);
  const active = MODES.find((m) => m.key === modeKey);

  function selectMode(key) {
    if (key === modeKey) return;
    setModeKey(key);
    setStarted(false);
  }

  return (
    <section
      id="demo"
      className="border-t border-slate-200 bg-[#F6F8F7] scroll-mt-24"
      aria-labelledby="business-walkthrough-title"
      data-testid="biz-demo-section"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 sm:py-20">
        <div className="max-w-3xl">
          <div className="text-xs uppercase tracking-[0.2em] text-emerald-800 font-semibold">{t("biz.proof.eyebrow")}</div>
          <h2 id="business-walkthrough-title" className="mt-2 font-display text-3xl sm:text-4xl font-bold text-slate-950">
            {t("biz.proof.title")}
          </h2>
        </div>

        <ModeSelector modeKey={modeKey} onSelect={selectMode} t={t} />

        {started ? (
          <WalkthroughPlayer key={modeKey} steps={active.steps} testIdBase={active.testIdBase} />
        ) : (
          <StartPreviewTeaser onStart={() => setStarted(true)} testIdBase={active.testIdBase} teaser={active.teaser} cta={t(active.ctaKey)} />
        )}

        <div className="mt-8 max-w-3xl mx-auto text-center text-sm text-slate-600">
          <Link to="/demo-business/scenarios" data-testid="biz-demo-browse-all"
                className="font-semibold text-[#1B4332] hover:underline inline-flex items-center gap-1.5">
            <PlayCircle className="w-4 h-4" aria-hidden="true" /> {t("biz.proof.browse_all")}
          </Link>
        </div>
      </div>
    </section>
  );
}

function ModeSelector({ modeKey, onSelect, t }) {
  return (
    <div className="mt-6 inline-flex flex-wrap items-center gap-1 rounded-full border border-slate-300 bg-white p-1"
         role="tablist" aria-label="Choose a demo" data-testid="biz-demo-mode-selector">
      {MODES.map((m) => (
        <button
          key={m.key}
          type="button"
          role="tab"
          aria-selected={modeKey === m.key}
          onClick={() => onSelect(m.key)}
          data-testid={`biz-demo-mode-${m.key}`}
          className={`min-h-9 px-4 py-1.5 rounded-full text-sm font-medium transition-colors ${
            modeKey === m.key ? "bg-[#1B4332] text-white" : "text-slate-600 hover:bg-slate-100"
          }`}
        >
          {t(m.labelKey)}
        </button>
      ))}
    </div>
  );
}

function StartPreviewTeaser({ onStart, testIdBase, teaser, cta }) {
  return (
    <div className="mt-6 rounded-3xl border border-slate-200 bg-white shadow-sm p-8 sm:p-12 text-center" data-testid={`${testIdBase}-start-teaser`}>
      <div className="mx-auto w-12 h-12 rounded-full bg-emerald-100 text-emerald-800 grid place-items-center">
        <PlayCircle className="w-5 h-5" aria-hidden="true" />
      </div>
      <p className="mt-4 text-sm text-slate-600 max-w-md mx-auto">{teaser}</p>
      <button
        type="button"
        onClick={onStart}
        data-testid={`${testIdBase}-start-preview`}
        className="mt-5 min-h-11 inline-flex items-center justify-center gap-2 rounded-lg bg-[#1B4332] px-5 py-2.5 text-sm font-semibold text-white hover:bg-[#143027] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-600 focus-visible:ring-offset-2"
      >
        <PlayCircle className="w-4 h-4" aria-hidden="true" /> {cta}
      </button>
    </div>
  );
}

function WalkthroughPlayer({ steps, testIdBase }) {
  const [stepIndex, setStepIndex] = useState(0);
  const [deviceMode, setDeviceMode] = useState("desktop");
  const [substep, setSubstep] = useState(null);
  const step = steps[stepIndex];
  const Icon = step.icon;
  const explainer = step.scenarioId ? OWNER_EXPLAINERS[step.scenarioId] : null;

  useEffect(() => { setSubstep(null); }, [step.scenarioId]);

  function goTo(index) {
    setStepIndex(Math.max(0, Math.min(steps.length - 1, index)));
  }

  // The outer story advances only after the embedded product flow reports
  // that its real final action completed: no timer-driven slideshow. A
  // small manual Previous/Next fallback exists below purely as a
  // stuck-visitor escape hatch, not the primary way through the story.
  const advanceTimerRef = useRef(null);

  useEffect(() => {
    function onMessage(e) {
      if (e.origin !== window.location.origin) return;
      const data = e.data;
      if (!data || data.scenarioId !== step.scenarioId) return;
      if (data.type === "CONFIRMPH_DEMO_SUBSTEP") {
        setSubstep({ heading: data.heading, instruction: data.instruction, context: data.context });
        return;
      }
      if (data.type !== "CONFIRMPH_DEMO_COMPLETE") return;
      clearTimeout(advanceTimerRef.current);
      advanceTimerRef.current = setTimeout(() => goTo(stepIndex + 1), 1800);
    }
    window.addEventListener("message", onMessage);
    return () => {
      window.removeEventListener("message", onMessage);
      clearTimeout(advanceTimerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step.scenarioId, stepIndex, steps.length]);

  return (
    <div className="mt-6" data-testid={`${testIdBase}-player`}>
      <div className="rounded-3xl bg-[#E9EFEC] p-3 sm:p-5 lg:p-6" data-testid={`${testIdBase}-active-step`}>
        <div className="mx-auto grid max-w-6xl gap-6 lg:grid-cols-12 lg:items-start">
          <div className="rounded-2xl bg-white p-5 sm:p-6 lg:col-span-5" data-testid={`${testIdBase}-step-explainer`}>
            <div className="flex items-center gap-3">
              <span className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-emerald-100 text-emerald-800">
                <Icon className="h-5 w-5" aria-hidden="true" />
              </span>
              <div>
                <div className="text-xs font-semibold uppercase tracking-[0.18em] text-emerald-800">{step.eyebrow}</div>
                <h3 className="mt-1 font-display text-xl font-semibold text-slate-950" data-testid={`${testIdBase}-step-title`}>{step.title}</h3>
              </div>
            </div>
            {substep ? (
              <div className="mt-4 space-y-4 text-sm leading-relaxed text-slate-600" data-testid={`${testIdBase}-substep-copy`}>
                <div><div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500">What you're seeing</div><p className="mt-1">{substep.heading}</p></div>
                <div><div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-emerald-800">Try it</div><p className="mt-1 font-medium text-slate-800">{substep.instruction}</p></div>
                {substep.context && <div><div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500">Why it matters</div><p className="mt-1">{substep.context}</p></div>}
              </div>
            ) : explainer ? (
              <div className="mt-4 space-y-4 text-sm leading-relaxed text-slate-600">
                <div><div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500">What you're seeing</div><p className="mt-1">{explainer.what}</p></div>
                <div><div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500">Why it helps</div><p className="mt-1">{explainer.why}</p></div>
                {explainer.tryIt && <div><div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-emerald-800">Try it</div><p className="mt-1 text-slate-700">{explainer.tryIt}</p></div>}
              </div>
            ) : step.isOutro ? (
              <p className="mt-4 text-sm leading-relaxed text-slate-600">Every screen in this walkthrough was the real ConfirmPH product: nothing left this page, and no real bookings were created.</p>
            ) : (
              <p className="mt-4 text-sm leading-relaxed text-slate-600">Follow the highlighted control on the right to continue.</p>
            )}
          </div>

          <div className="min-w-0 lg:col-span-7">
            <div className="mb-3 flex justify-center sm:justify-end">
              <PreviewDeviceToggle mode={deviceMode} setMode={setDeviceMode} testIdBase={testIdBase} />
            </div>
            {step.isOutro ? (
              <BrandOutroFrame mode={deviceMode} testIdBase={testIdBase} />
            ) : (
              <ScenarioFrame mode={deviceMode} scenarioId={step.scenarioId} testIdBase={testIdBase} />
            )}
          </div>
        </div>

        {/* Manual fallback: the story is meant to advance on its own once the
            real action inside completes, but if that never fires (a missed
            click, a scenario that doesn't complete cleanly) there must still
            be a way to move. Deliberately small and secondary, not a
            replacement for the real interaction. */}
        <div className="mt-4 flex items-center justify-center gap-3 text-sm text-slate-500" data-testid={`${testIdBase}-manual-nav`}>
          <button
            type="button"
            onClick={() => goTo(stepIndex - 1)}
            disabled={stepIndex === 0}
            data-testid={`${testIdBase}-prev`}
            className="inline-flex items-center gap-1 rounded-full border border-slate-300 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <ChevronLeft className="w-3.5 h-3.5" /> Previous
          </button>
          <span className="text-xs text-slate-400">{stepIndex + 1} / {steps.length} · stuck? use these</span>
          <button
            type="button"
            onClick={() => goTo(stepIndex + 1)}
            disabled={stepIndex === steps.length - 1}
            data-testid={`${testIdBase}-next`}
            className="inline-flex items-center gap-1 rounded-full border border-slate-300 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            Next <ChevronRight className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>
    </div>
  );
}

const NATIVE_DESKTOP_WIDTH = 1120;
const NATIVE_DESKTOP_HEIGHT = 640;
const NATIVE_PHONE_WIDTH = 365;   // 353 screen + 6px border each side
const PHONE_SCREEN_WIDTH = 353;
const PHONE_CHROME_HEIGHT = 66;   // status bar + border + rounded-corner chrome around the screen
const PHONE_CONTENT_HEIGHT = 700;

// Priority-ordered "what should a first-time visitor click next" targets,
// checked top to bottom: the first one present in the current screen gets
// the pulsing red spotlight. Mirrors exactly the buttons tryAdvanceInnerContent
// already knows how to drive for the linear cases (intro/custom/panel), plus
// the deeper interactive-flow buttons those can't auto-click (typing/sending/
// confirming), so guidance stays available all the way through a scenario.
// Entries ending in "-" are dynamic (per-vehicle/per-booking) testids
// matched by prefix, e.g. dashboard-mark-maintenance-${plate}: everything
// else is matched exactly.
const SPOTLIGHT_CANDIDATES = [
  "incident-chat-continue",
  "walkthrough-chat-continue",
  "incident-vehicle-status-action",
  "incident-type-option-accident",
  "incident-severity-option-critical",
  "incident-log-record",
  "incident-damage-save",
  "warning-notify-form-incident-warning-vb-1-submit",
  "warning-notify-incident-warning-vb-1",
  "reassign-choose-smart",
  "reassign-continue",
  "reassign-submit",
  "reassign-no-suitable-replacement",
  "incident-send-reconfirmation",
  "pending-change-accept-btn",
  "incident-resolution-continue",
  "incident-cancel-booking",
  "incident-cancel-continue",
  "walkin-intro-start-workflow",
  "walkin-intro-next",
  "panel-primary-action",
  "handover-next",
  "handover-complete-btn",
  "return-record-btn",
  "return-inspection-start-btn",
  "insp-finalize",
  "return-open-next-booking-btn",
  "bookings-open-",
  "booking-primary-action",
  "reminder-send-",
  "checklist-start-",
  "checklist-submit-btn",
  "vehicle-view-",
  "vehicle-detail-primary",
  "maint-save",
  "warning-primary-",
  "review-invite-send",
  "confirmation-preview-copy",
  "channel-open-preview",
  "channel-send-link",
  "notepad-create-draft",
  "draft-create-booking",
  "booking-send-confirmation",
  "send-link-copy",
  "customer-confirm-btn",
  "booking-record-balance",
  "dashboard-reassign-smart",
  "dashboard-add-incoming-draft",
  "dashboard-mark-maintenance-",
  "channel-open-notepad",
  "dashboard-draft-card",
  "dashboard-booking-card-",
  "demo-next",
];
const SPOTLIGHT_CLASS = "__biz_walkthrough_spotlight";
const SPOTLIGHT_STYLE_ID = "__biz_walkthrough_spotlight_style";

function injectSpotlightStyles(doc) {
  if (doc.getElementById(SPOTLIGHT_STYLE_ID)) return;
  const style = doc.createElement("style");
  style.id = SPOTLIGHT_STYLE_ID;
  style.textContent = `
    .${SPOTLIGHT_CLASS} {
      position: relative;
      outline: 3px solid #ef4444 !important;
      outline-offset: 3px;
      border-radius: 10px;
      animation: __biz_spotlight_pulse 1.1s ease-in-out infinite;
      z-index: 40;
    }
    @keyframes __biz_spotlight_pulse {
      0%, 100% { box-shadow: 0 0 0 0 rgba(239, 68, 68, 0.55); }
      50% { box-shadow: 0 0 0 7px rgba(239, 68, 68, 0); }
    }
  `;
  doc.head.appendChild(style);
}

// Returns true if it found and highlighted something inside the iframe.
// Written to be idempotent (no DOM write at all when the target hasn't
// changed) because the MutationObserver driving this watches attribute
// changes too, including our own class toggle: a non-idempotent version
// would retrigger itself every call, burning CPU in a tight loop for the
// whole animation and making the page feel like it's "bugging out".
function updateSpotlight(doc) {
  let target = null;
  for (const testId of SPOTLIGHT_CANDIDATES) {
    const isPrefix = testId.endsWith("-");
    const el = isPrefix
      ? doc.querySelector(`[data-testid^="${testId}"]`)
      : doc.querySelector(`[data-testid="${testId}"]`);
    if (el && !el.disabled && el.offsetParent !== null) { target = el; break; }
  }
  const current = doc.querySelector(`.${SPOTLIGHT_CLASS}`);
  if (current === target) return !!target;
  current?.classList.remove(SPOTLIGHT_CLASS);
  target?.classList.add(SPOTLIGHT_CLASS);
  return !!target;
}

/**
 * Loads the real, unmodified /demo-business/scenarios/:id page in an
 * iframe: the same "own real viewport, not a shrunk div" technique
 * walkthrough2/DeviceFrame.jsx uses for the renter walkthrough. Unlike
 * DeviceFrame, no postMessage handshake is needed: each scenario page is
 * fully self-contained and reads its own URL, so switching `scenarioId`
 * just changes the iframe `src` and the page loads its own state.
 *
 * The device viewport is deliberately fixed. Dynamic chat messages and
 * changing workflow screens scroll inside this model instead of changing
 * the iframe height and forcing the surrounding homepage to reflow.
 */
function ScenarioFrame({ mode, scenarioId, testIdBase }) {
  // `embed=1` scopes every homepage-embed-only behavior (stripped chrome,
  // faster + resuming chat, no end-of-scenario recap) to just this
  // walkthrough: see the note at the top of this file for the full chain.
  const src = `/demo-business/scenarios/${scenarioId}?embed=1`;
  const contentHeight = mode === "phone" ? PHONE_CONTENT_HEIGHT : NATIVE_DESKTOP_HEIGHT;
  const spotlightObserverRef = useRef(null);

  useEffect(() => {
    return () => {
      spotlightObserverRef.current?.disconnect();
      spotlightObserverRef.current = null;
    };
  }, [scenarioId, mode]);

  function handleIframeLoad(e) {
    let doc;
    try { doc = e.currentTarget.contentDocument; } catch { return; }
    if (!doc || !doc.documentElement) return;
    injectSpotlightStyles(doc);
    doc.documentElement.style.height = "100%";
    doc.documentElement.style.overflow = "hidden";
    if (doc.body) {
      doc.body.style.height = "100%";
      doc.body.style.overflowY = "auto";
      doc.body.style.overscrollBehavior = "contain";
    }
    updateSpotlight(doc);
    // The spotlight target can change screen-to-screen (e.g. chat → notepad)
    // without the container's overall height changing, so ResizeObserver
    // alone would miss it: a MutationObserver catches every DOM swap.
    // Lightly debounced since typing/chat reveals mutate the DOM often.
    if (typeof MutationObserver !== "undefined") {
      spotlightObserverRef.current?.disconnect();
      let pending = null;
      const mo = new MutationObserver(() => {
        if (pending) return;
        pending = setTimeout(() => {
          pending = null;
          updateSpotlight(doc);
        }, 150);
      });
      mo.observe(doc.body, { childList: true, subtree: true, attributes: true });
      spotlightObserverRef.current = mo;
    }
  }

  const iframeEl = (
    <iframe
      title={`ConfirmPH business demo: ${scenarioId}`}
      src={src}
      onLoad={handleIframeLoad}
      className="w-full h-full border-0 bg-white"
      data-testid={`${testIdBase}-${mode}-iframe`}
    />
  );

  if (mode === "phone") {
    return (
      <div className="h-[720px] max-h-[72vh]" data-testid={`${testIdBase}-phone-viewport-slot`}>
        <FitBox nativeWidth={NATIVE_PHONE_WIDTH} nativeHeight={contentHeight + PHONE_CHROME_HEIGHT} testId={`${testIdBase}-phone-frame`}>
          <div className="rounded-[2rem] border-[6px] border-slate-900 bg-slate-900 shadow-xl" style={{ width: PHONE_SCREEN_WIDTH }}>
            <div className="flex items-center justify-between px-4 pt-2 pb-1 text-[10px] text-white">
              <span className="font-mono">9:41</span>
              <span className="font-mono opacity-80">DEMO · ••• 5G ▄▅▇</span>
            </div>
            <div className="bg-white rounded-t-2xl overflow-hidden" style={{ height: contentHeight, width: PHONE_SCREEN_WIDTH, margin: "0 auto" }}>
              {iframeEl}
            </div>
          </div>
        </FitBox>
      </div>
    );
  }

  return (
    <div className="h-[640px] max-h-[72vh] rounded-xl border border-slate-200 bg-white shadow-sm overflow-hidden" data-testid={`${testIdBase}-desktop-frame`}>
      <FitBox nativeWidth={NATIVE_DESKTOP_WIDTH} nativeHeight={contentHeight} testId={`${testIdBase}-desktop-scaled-viewport`}>
        {iframeEl}
      </FitBox>
    </div>
  );
}

/**
 * The branded closing beat: the SAME device chrome (phone bezel / desktop
 * frame) as ScenarioFrame, but rendering BrandOutro directly instead of an
 * iframe - the outro is a static asset, not real product UI, so it doesn't
 * need the real-viewport-iframe technique. Reused for both the business
 * normal and business issue flows' final step (see MAIN_STEPS/ISSUE_STEPS).
 */
function BrandOutroFrame({ mode, testIdBase }) {
  const screenTestId = `${testIdBase}-outro-screen`;
  const videoTestId = `${testIdBase}-outro-video`;
  if (mode === "phone") {
    return (
      <div className="h-[720px] max-h-[72vh]" data-testid={`${testIdBase}-phone-viewport-slot`}>
        <FitBox nativeWidth={NATIVE_PHONE_WIDTH} nativeHeight={PHONE_CONTENT_HEIGHT + PHONE_CHROME_HEIGHT} testId={`${testIdBase}-phone-frame`}>
          <div className="rounded-[2rem] border-[6px] border-slate-900 bg-slate-900 shadow-xl" style={{ width: PHONE_SCREEN_WIDTH }}>
            <div className="flex items-center justify-between px-4 pt-2 pb-1 text-[10px] text-white">
              <span className="font-mono">9:41</span>
              <span className="font-mono opacity-80">DEMO · ••• 5G ▄▅▇</span>
            </div>
            <div className="rounded-t-2xl overflow-hidden" style={{ height: PHONE_CONTENT_HEIGHT, width: PHONE_SCREEN_WIDTH, margin: "0 auto" }}>
              <BrandOutro deviceMode="phone" screenTestId={screenTestId} videoTestId={videoTestId} />
            </div>
          </div>
        </FitBox>
      </div>
    );
  }
  return (
    <div className="h-[640px] max-h-[72vh] rounded-xl border border-slate-200 bg-white shadow-sm overflow-hidden" data-testid={`${testIdBase}-desktop-frame`}>
      <FitBox nativeWidth={NATIVE_DESKTOP_WIDTH} nativeHeight={NATIVE_DESKTOP_HEIGHT} testId={`${testIdBase}-desktop-scaled-viewport`}>
        <BrandOutro deviceMode="desktop" screenTestId={screenTestId} videoTestId={videoTestId} />
      </FitBox>
    </div>
  );
}

/**
 * Scales a fixed-size child to fit whatever box its parent actually gives
 * it: measuring both width AND height (unlike a width-only scale, which
 * would let the child's height exceed a height-constrained parent and
 * force the outer page to scroll). Never scales up past 1x (a transform
 * scale >1 on an iframe just blurs its rendered pixels), so on generous
 * screens the device frame sits centered at its natural size instead of
 * being stretched.
 */
function FitBox({ nativeWidth, nativeHeight, children, testId }) {
  const outerRef = useRef(null);
  const [scale, setScale] = useState(1);

  useLayoutEffect(() => {
    const el = outerRef.current;
    if (!el) return;
    function measure() {
      const { width, height } = el.getBoundingClientRect();
      if (width > 0 && height > 0) {
        setScale(Math.min(width / nativeWidth, height / nativeHeight, 1));
      }
    }
    measure();
    if (typeof ResizeObserver !== "undefined") {
      // Deferred to the next frame for the same reason as ScenarioFrame's
      // content-height observer: avoids tripping the browser's
      // ResizeObserver loop-limit during rapid layout changes.
      let rafId = null;
      const ro = new ResizeObserver(() => {
        if (rafId != null) return;
        rafId = requestAnimationFrame(() => { rafId = null; measure(); });
      });
      ro.observe(el);
      return () => { if (rafId != null) cancelAnimationFrame(rafId); ro.disconnect(); };
    }
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, [nativeWidth, nativeHeight]);

  return (
    <div ref={outerRef} className="relative w-full h-full flex items-start justify-center overflow-hidden" data-testid={testId}>
      <div className="relative shrink-0" style={{ width: nativeWidth * scale, height: nativeHeight * scale }}>
        <div className="absolute left-0 top-0" style={{ width: nativeWidth, height: nativeHeight, transform: `scale(${scale})`, transformOrigin: "top left" }}>
          {children}
        </div>
      </div>
    </div>
  );
}

function PreviewDeviceToggle({ mode, setMode, testIdBase }) {
  return (
    <div className="inline-flex items-center rounded-full border border-slate-300 bg-white p-0.5"
         data-testid={`${testIdBase}-device-toggle`} role="group" aria-label="Preview device">
      <button type="button" onClick={() => setMode("desktop")}
              data-testid={`${testIdBase}-device-desktop`}
              className={`inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-full transition-colors ${mode === "desktop" ? "bg-[#1B4332] text-white" : "text-slate-600 hover:bg-slate-100"}`}
              title="Show the preview as a desktop browser">
        <Monitor className="w-3.5 h-3.5" /> Desktop
      </button>
      <button type="button" onClick={() => setMode("phone")}
              data-testid={`${testIdBase}-device-phone`}
              className={`inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-full transition-colors ${mode === "phone" ? "bg-[#1B4332] text-white" : "text-slate-600 hover:bg-slate-100"}`}
              title="Show the preview as a real phone viewport">
        <Smartphone className="w-3.5 h-3.5" /> Phone
      </button>
    </div>
  );
}
